<template>
	<div>
		<component v-if="payload" :is="isForm ? 'vue-form' : 'div'" v-on:submit="save" size="xsm">

			<slot></slot>

			<div v-if="'UserAsset' in payload && payload.UserAsset" class="text-muted mb-3">
				{{customerName ? customerName : "The customer"}} has accepted a link with your company.<br>
				Some fields in this record may be locked, and extracted from their linked data.
			</div>

			<field
				label="Customer"
				col-class="col-sm-5"
				:params="{type:'select',options:$store.customerOptions}"
				:readonly="'UserAsset' in payload && payload.UserAsset"
				v-model="payload.contact_id"
				v-on:change="customerChange">
			</field>

			<field v-if="payload.contact_id"
				label="Customer Location"
				col-class="col-sm-5"
				:params="{type:'select',options:customerLocationOptions}"
				v-model="payload.location_id">
			</field>

			<field
				label="Category"
				col-class="col-sm-5"
				:params="{type:'select',options:categoryOptions}"
				v-model="payload.category_id"
				v-on:change="setCustomFieldList">
			</field>

			<hr class="mb-4">

			<div class="d-flex justify-content-between align-items-center">

				<h4>Asset Template</h4>

				<button type="button" class="btn btn-primary btn-sm" v-on:click="templateModalVisible = true">
					<i class="fa fa-sync-alt"></i>
					Change
				</button>

			</div>

			<field
				label="Current Template"
				col-class="col-5"
				:readonly="true"
				v-model="templateName">
			</field>

			<hr>

			<field
				label="Description"
				col-class="col-sm-5"
				v-model="isFieldDefinedByCustomer('description') ? payload.UserAsset.description : payload.description"
				:readonly="isFieldDefinedByCustomer('description')"
				:invalid="$store.errorList.description">
			</field>

			<field
				label="Serial #"
				col-class="col-sm-5"
				v-model="isFieldDefinedByCustomer('serial_num') ? payload.UserAsset.serial_num : payload.serial_num"
				:readonly="isFieldDefinedByCustomer('serial_num')"
				:invalid="$store.errorList.serial_num">
			</field>

			<field
				label="Date Acquired"
				col-class="col-sm-5"
				:params="{type:'date'}"
				:readonly="isFieldDefinedByCustomer('date_acquired')"
				v-model="isFieldDefinedByCustomer('date_acquired') ? payload.UserAsset.date_acquired : payload.date_acquired"
				:invalid="$store.errorList.date_acquired">
			</field>

			<field
				label="Notes"
				col-class="col-sm-5"
				:params="{type:'textarea', size:'md'}"
				:readonly="isFieldDefinedByCustomer('notes')"
				v-model="isFieldDefinedByCustomer('notes') ? payload.UserAsset.notes : payload.notes"
				:invalid="$store.errorList.notes">
			</field>

			<field
				label="Form Child Templates"
				col-class="col-sm-5"
				:params="{ type: 'select-multi', options: childTemplateOptions }"
				:readonly="isFieldDefinedByCustomer('FormChildTemplate')"
				v-model="selectedFormChildTemplateIds"
				:invalid="$store.errorList.FormChildTemplate">
			</field>

			<field
				label="Checklists"
				col-class="col-sm-5"
				:params="{ type: 'select-multi', options: checklistOptions }"
				:readonly="isFieldDefinedByCustomer('Checklist')"
				v-model="selectedChecklistIds"
				:invalid="$store.errorList.Checklist">
			</field>

			<template v-if="customFieldList && customFieldList.length">

				<hr>

				<template v-for="field in customFieldList">
					<template v-if="field.type == 'select'">
						<field
							col-class="col-sm-5"
							:key="field.label"
							:label="field.label"
							:params="{type:field.type,required:field.required > 0,options:optionifyCommaList(field.options)}"
							v-model="field.value">
						</field>
					</template>

					<field
						v-else
						col-class="col-sm-5"
						:key="field.label"
						:label="field.label"
						:params="{type:field.type,required:field.required > 0}"
						v-model="field.value">
					</field>
				</template>

			</template>

			<hr class="mb-4">

			<h4>Next Service</h4>

			<field
				label="Description"
				col-class="col-sm-5"
				:readonly="isFieldDefinedByCustomer('next_service_description')"
				v-model="isFieldDefinedByCustomer('next_service_description') ? payload.UserAsset.next_service_description : payload.next_service_description"
				:invalid="$store.errorList.next_service_description">
			</field>

			<form-group label="Due (date & odo/hrs)"
				col-class="col-sm-5">

				<div class="d-flex flex-row">
					<field
						:params="{type:'date'}"
						:readonly="isFieldDefinedByCustomer('next_service_date')"
						v-model="isFieldDefinedByCustomer('next_service_date') ? payload.UserAsset.next_service_date : payload.next_service_date"
						:invalid="$store.errorList.next_service_date">
					</field>

					<field
						:params="{type:'number',placeholder:'mileage or hrs'}"
						:readonly="isFieldDefinedByCustomer('next_service_mileage_or_time')"
						v-model="isFieldDefinedByCustomer('next_service_mileage_or_time') ? payload.UserAsset.next_service_mileage_or_time : payload.next_service_mileage_or_time"
						:invalid="$store.errorList.next_service_mileage_or_time">
					</field>
				</div>

			</form-group>

			<hr class="mb-4">

			<h4>Last Service</h4>

			<field
				label="Description"
				col-class="col-sm-5"
				:readonly="isFieldDefinedByCustomer('last_service_description')"
				v-model="isFieldDefinedByCustomer('last_service_description') ? payload.UserAsset.last_service_description : payload.last_service_description"
				:invalid="$store.errorList.last_service_description">
			</field>

			<form-group label="Done (date & odo/hrs)"
				col-class="col-sm-5">

				<div class="d-flex flex-row">
					<field
						:params="{type:'date'}"
						:readonly="isFieldDefinedByCustomer('last_service_date')"
						v-model="isFieldDefinedByCustomer('last_service_date') ? payload.UserAsset.last_service_date : payload.last_service_date"
						:invalid="$store.errorList.last_service_date">
					</field>

					<field
						:params="{type:'number',placeholder:'mileage or hrs'}"
						:readonly="isFieldDefinedByCustomer('last_service_mileage_or_time')"
						v-model="isFieldDefinedByCustomer('last_service_mileage_or_time') ? payload.UserAsset.last_service_mileage_or_time : payload.last_service_mileage_or_time"
						:invalid="$store.errorList.last_service_mileage_or_time">
					</field>
				</div>

			</form-group>

			<hr>

			<template slot="buttons-left">
				<button class="btn btn-outline-danger" v-on:click="deleteConfirmModalVisible = true" v-if="'id' in payload">
					<i class="fa fa-trash"></i>
					Delete Asset
				</button>
			</template>

		</component>

		<comment-thread v-if="payload && 'slug' in payload"
			style="background-color: transparent;"
			:thread-id="'message_thread_id' in payload ? payload.message_thread_id : false"
			:parent-slug="payload.slug"
			parent-model="CustomerAsset"
			:thread-subject-reference="threadSubjectReference">
		</comment-thread>

		<modal-dialog
			:modal-visible="deleteConfirmModalVisible"
			title="Delete Asset"
			confirm-button-text="Yes, Delete"
			button-class="btn-outline-danger"
			v-on:closeModal="deleteConfirmModalVisible = false"
			v-on:confirm="deleteAsset">
			Are you sure you want to delete this asset and all it's maintenance records?
		</modal-dialog>

		<customer-asset-template-selector-modal
			:visible="templateModalVisible"
			v-on:addTemplate="changeTemplate"
			v-on:closeModal="templateModalVisible = false">
		</customer-asset-template-selector-modal>

		<modal-dialog
			:modal-visible="templateConfirmModalVisible"
			title="Confirm Template"
			confirm-button-text="Confirm"
			v-on:closeModal="templateConfirmModalVisible = false"
			v-on:confirm="setTemplate">

			Changing this asset's template will overwrite the following:

			<ul class="mt-2">
				<li>Any assigned parts</li>
				<li>Any assigned custom fields</li>
				<li>Any assigned child form templates</li>
				<li>Any assigned checklists</li>
			</ul>

			Are you sure that you would like to proceed?

		</modal-dialog>

	</div>
</template>
<script type="text/javascript">
import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"
import CommentThread from "@/components/super_components/CommentThread"
import VueForm from "@/components/VueForm"
import CustomerAssetTemplateSelectorModal from "@/components/super_components/CustomerAssetTemplateSelectorModal"


export default {
	name: "CustomerAssetEditForm",
	components: {
		ModalDialog,
		Field,
		FormGroup,
		CommentThread,
		VueForm,
		CustomerAssetTemplateSelectorModal
	},
	props: ["record","isForm","assetSlug","prefilledFields"],
	data() {
		return {
			payload: {},
			customFieldList: [],
			customerLocations: [],
			categories: [],
			childTemplates: [],
			checklists: [],
			deleteConfirmModalVisible: false,

			selectedChecklistIds: [],
			selectedFormChildTemplateIds: [],

			templateModalVisible: false,
			templateName: "",
			templateConfirmModalVisible: false,
			newTemplate: null,
		}
	},
	computed: {
		checklistOptions() {
			return this.optionify(this.checklists, 'name')
		},
		childTemplateOptions() {
			return this.optionify(this.childTemplates, 'description')
		},
		customerName() {
			if (!this.$store.customers || !this.payload)
				return false

			const customer = this.$store.customers.find(c => c.id == this.payload.contact_id)
			return customer ? customer.name : false
		},
		threadSubjectReference() {
			if (this.payload && "description" in this.payload)
				return "Customer Asset: "+this.payload.description
			else
				return "Customer Asset"
		},
		categoryOptions() {
			return this.optionify(this.categories,"name")
		},
		customerLocationOptions() {
			let options = this.optionify(this.customerLocations.map(location => {
				location.display = location.location_name+" "+location.address
				return location
			}),"display")

			options.unshift({value: 0,text:'Main Location'})

			return options
		}
	},
	watch: {
		record() {
			this.setPayload(this.record)
			this.setPrefilledFields()
			this.initCustomerAsset()
		},
		visible() {
			this.initCustomerAsset()
			this.setPrefilledFields()
		},
		prefilledFields() {
			this.setPrefilledFields()
		}
	},
	methods: {

		setTemplate() {
			const vm = this

			let partsJSON = ""
			if ('Material' in this.newTemplate && this.newTemplate.Material && this.newTemplate.Material.length)
				partsJSON = JSON.stringify(this.newTemplate.Material)

			const customFieldsJSON = this.newTemplate.custom_fields_json

			// Update payload
			this.payload.parts_json = partsJSON
			this.payload.custom_fields_json = customFieldsJSON

			const newChecklist = ('Checklist' in this.newTemplate && this.newTemplate.Checklist) ?
				this.newTemplate.Checklist : []

			this.payload.Checklist = newChecklist
			this.selectedChecklistIds = newChecklist.map(checklist => checklist.id)

			const newFormChildTemplate = ('FormChildTemplate' in this.newTemplate && this.newTemplate.FormChildTemplate) ?
				this.newTemplate.FormChildTemplate : []

			this.payload.FormChildTemplate = newFormChildTemplate
			this.selectedFormChildTemplateIds = newFormChildTemplate.map(fct => fct.id)

			// Update current template name and custom fields
			this.templateName = this.newTemplate.description
			this.customFieldList = (customFieldsJSON) ? JSON.parse(customFieldsJSON) : []

			this.payload.template_id = this.newTemplate.id

			// Close all modals
			this.templateConfirmModalVisible = false
			this.templateModalVisible = false

			this.save()
		},

		changeTemplate(template) {

			const params = {
				include: [
					{ model: 'Material',          fields: ['id', 'description', 'part_number'] },
					{ model: 'Checklist',         fields: ['id'] },
					{ model: 'FormChildTemplate', fields: ['id'] },
				]
			}

			this.getJSON('CustomerAssetTemplate/find/' + template.id, params).then(response => {
				if (response.success) {
					this.templateConfirmModalVisible = true
					this.newTemplate = response.data
				} else {
					this.alertErrors(response.errors)
				}
			})
		},

		setTemplateName() {
			if (this.payload && 'CustomerAssetTemplate' in this.payload && this.payload.CustomerAssetTemplate &&
				'description' in this.payload.CustomerAssetTemplate && this.payload.CustomerAssetTemplate.description)
			{
				this.templateName = this.payload.CustomerAssetTemplate.description
			} else {
				this.templateName = "No Template"
			}
		},

		setPayload(record) {
			this.payload = this.cloneObj(record)

			if (this.payload.contact_id > 0)
				this.fetchCustomerLocations(this.payload.contact_id)
		},
		isFieldDefinedByCustomer(field) {
			return 'UserAsset' in this.payload && this.payload.UserAsset &&
				field in this.payload.UserAsset && this.payload.UserAsset[field] &&
				field in this.payload &&
				this.payload.UserAsset[field] != this.payload[field]
		},

		setPrefilledFields() {
			if (!this.payload.contact_id) {
				this.payload.contact_id = this.prefilledFields.customer_id

				this.fetchCustomerLocations(this.payload.contact_id)
			}
			
			if (!this.payload.location_id)
				this.payload.location_id = this.prefilledFields.customer_location_id

			if (!this.payload.category_id)
				this.payload.category_id = this.prefilledFields.category_id
		},
		deleteAsset() {
			let vm = this

			this.postData("CustomerAsset/delete",{id: this.payload.id}).then(response => {
				if (response.success) {
					vm.deleteConfirmModalVisible = false
					vm.alertSuccess("Asset deleted!")

					//go to customer assets
					vm.$router.push("/contact/"+contactSlug+"/assets")
				}
			})
		},
		optionifyCommaList(list) {
			if (list) {
				let items = list.split(",")

				return items.map(item => {
					return {
						value: item,
						text: item
					}
				})
			}
			else
				return []
		},
		customerChange(customer_id) {
			let vm = this

			this.fetchCustomerLocations(customer_id)
		},
		setCustomFieldList() {
			let vm = this

			this.$nextTick(() => {
				vm.customFieldList = (vm.payload.custom_fields_json) ? JSON.parse(vm.payload.custom_fields_json) : []
			})
		},
		save() {
			let vm = this

			this.payload.custom_fields_json = JSON.stringify(this.customFieldList)

			let newPayload = this.cloneObj(this.payload)
			Object.keys(newPayload).forEach(key => {
				if (typeof(newPayload[key]) == 'object' && newPayload[key] !== null) {
					delete newPayload[key]
				}
			})


			let removeFields = ["parts_json"]
			removeFields.forEach(key => {
				delete newPayload[key]
			})		

			newPayload.Checklist = this.selectedChecklistIds
			newPayload.FormChildTemplate = this.selectedFormChildTemplateIds

			this.saveRecord("CustomerAsset",newPayload,response => {
				vm.alertSaved("Asset saved!")
				vm.$emit("closeModal")
				vm.$emit("modelChanged")

				if (vm.assetSlug == 'new')
					vm.$router.push("/contact/asset/"+response.data.slug+"/log")
				else
					vm.fetchAsset().then()
			})
		},
		closeModal() {
			this.$emit("closeModal")
		},
		setSelectedFormChildTemplates() {
			this.selectedFormChildTemplateIds = []
			if ('FormChildTemplate' in this.payload && this.payload.FormChildTemplate)
				this.selectedFormChildTemplateIds = this.payload.FormChildTemplate.map(fct => fct.id)
		},
		setSelectedChecklists() {
			this.selectedChecklistIds = []
			if ('Checklist' in this.payload && this.payload.Checklist)
				this.selectedChecklistIds = this.payload.Checklist.map(checklist => checklist.id)
		},
		fetchAsset() {
			const vm = this

			const payload = {
				include: [
					{ model: "UserAsset", include: ["UserLocation"] },
					{
						model: "ContactLocation",
						include: [{model:"Contact",fields:["id","slug"]}]
					},
					{
						model: 'CustomerAssetTemplate',
						include: [
							{ model: 'Checklist', fields: ['id'] },
							{ model: 'FormChildTemplate', fields: ['id'] },
						]
					},
					{ model: 'Checklist', fields: ['id'] },
					{ model: 'FormChildTemplate', fields: ['id'] },
					'Contact',
				]
			}

			return new Promise(resolve => {
				this.getJSON("CustomerAsset/find/"+this.assetSlug, payload).then(response => {
					if (response && response.success) {
						vm.setPayload(response.data)
						vm.initCustomerAsset()

						vm.customerChange(vm.payload.contact_id)
						vm.$emit("assetFetched", response.data)
					}

					resolve()
				})
			})
		},
		initCustomerAsset() {
			this.setTemplateName()
			this.setCustomFieldList()
			this.setSelectedChecklists()
			this.setSelectedFormChildTemplates()
		},
		fetchFormChildTemplates() {
			const vm = this

			const payload = {
				fields: ['id', 'description']
			}

			return new Promise(resolve => {
				this.getJSON("FormChildTemplate/get", payload).then(response => {
					if (response && response.success)
						vm.childTemplates = response.data
					resolve()
				})
			})
		},
		fetchCustomerLocations(customerId) {
			let vm = this

			return new Promise(resolve => {
				let params = {
					filters: [
						["contact_id",customerId]
					]
				}

				vm.getJSON("ContactLocation/get",params).then(response => {
					if (response.success)
						vm.customerLocations = response.data

					console.log(vm.customerLocations.length)

					resolve(response)
				})
			})
		},
		fetchData() {
			let vm = this

			vm.spinner(true)

			let promises = [
				this.getJSON("CustomerAssetCategory/get"),
				this.getJSON("Checklist/get", {orders: [['name', 'ASC']]}),
				this.fetchCustomers(true,false),
				this.fetchFormChildTemplates(),
			]

			if (!this.record && this.assetSlug && this.assetSlug != 'new')
				promises.push(this.fetchAsset())

			if (this.payload.contact_id > 0)
				promises.push(this.fetchCustomerLocations(this.payload.contact_id))

			Promise.all(promises).then(responses => {
				vm.categories = responses[0].data
				vm.checklists = responses[1].data
				vm.spinner(false)
			})
		}
	},
	created() {
		if (this.record)
			this.setPayload(this.record)

		if (this.assetSlug == 'new')
			this.setPayload({})

		this.fetchData()
		this.setPrefilledFields()
		this.initCustomerAsset()
	}
}
</script>